import {
  createContext,
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  type RefObject,
  type MutableRefObject,
  type SetStateAction,
  type Dispatch,
  type ReactNode,
} from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { Feature, Geometry } from "geojson";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "../styles/mapbox-draw-custom.css";
import { fetchArcgisLayers } from "@/services/layerService";
import type { TaskGeometry } from "@/types/task";
import type { ArcgisLayerConfig } from "@/types/layers";
import {
  TASK_LAYER_KEY,
  basemaps,
  BASEMAP_STORAGE_KEY,
} from "@/constant/layers";

export type HighlightedFeature = {
  feature: Feature<Geometry>;
  layerType: "task" | "arcgis" | "dynamic";
  sourceId?: string;
};

type DrawMode = "point" | "line" | "polygon";

// Mapbox GL Draw feature type
type DrawFeature = {
  id: string;
  type: "Feature";
  geometry: TaskGeometry;
  properties?: Record<string, unknown>;
};

const removeDrawFeatureSafely = (
  draw: MapboxDraw | null,
  featureId: string | null | undefined,
  context?: string
) => {
  if (!draw || !featureId) return;

  try {
    draw.delete(featureId);
  } catch (error) {
    const message = context
      ? `Failed to remove drawn feature (${context}):`
      : "Failed to remove drawn feature:";
    console.warn(message, error);
  }
};

interface MapContextProps {
  mapRef: RefObject<HTMLDivElement | null>;
  mapInstance: MutableRefObject<MapLibreMap | null>;
  registerMapInstance: (map: MapLibreMap) => void;
  unregisterMapInstance: () => void;
  showBasemapSwitcher: boolean;
  setShowBasemapSwitcher: Dispatch<SetStateAction<boolean>>;
  tableVisible: boolean;
  setTableVisible: Dispatch<SetStateAction<boolean>>;
  taskLayerVisible: boolean;
  setTaskLayerVisible: Dispatch<SetStateAction<boolean>>;
  selectedBasemapKey: string;
  setSelectedBasemapKey: Dispatch<SetStateAction<string>>;
  arcgisLayersVisible: Record<string, boolean>;
  toggleArcgisLayer: (layerKey: string) => void;
  getVisibleArcgisLayers: () => ArcgisLayerConfig[];
  refreshLayers: () => Promise<void>;
  isDrawing: boolean;
  isDrawingRef: MutableRefObject<boolean>; // Add ref for synchronous access
  drawnGeometry: TaskGeometry | null;
  drawnFeature: DrawFeature | null;
  startDrawing: (type: DrawMode, source?: "iconbar" | "layer") => void;
  stopDrawing: () => void;
  clearDrawnGeometry: () => void;
  removeDrawnLayer: () => void;
  onGeometryDrawn?: (geometry: TaskGeometry) => void;
  setOnGeometryDrawn: (
    callback: ((geometry: TaskGeometry) => void) | undefined
  ) => void;
  drawingSource: "iconbar" | "layer" | null;
  highlightedFeature: HighlightedFeature | null;
  setHighlightedFeature: (feature: HighlightedFeature | null) => void;
  clearHighlight: () => void;
}

const MapContext = createContext<MapContextProps | undefined>(undefined);
export { MapContext };

export function MapProvider({ children }: { children: ReactNode }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<MapLibreMap | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const drawCleanupRef = useRef<(() => void) | null>(null);

  const [showBasemapSwitcher, setShowBasemapSwitcher] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const [taskLayerVisible, setTaskLayerVisible] = useState(true);
  const [selectedBasemapKey, setSelectedBasemapKey] = useState(() => {
    if (typeof window === "undefined") {
      return basemaps[0].key;
    }
    return localStorage.getItem(BASEMAP_STORAGE_KEY) ?? basemaps[0].key;
  });

  const [isDrawing, setIsDrawing] = useState(false);
  const isDrawingRef = useRef(false); // Use ref for synchronous access in event handlers
  const [drawnGeometry, setDrawnGeometry] = useState<TaskGeometry | null>(null);
  const [drawnFeature, setDrawnFeature] = useState<DrawFeature | null>(null);
  const drawnFeatureRef = useRef<DrawFeature | null>(null);
  const onGeometryDrawnRef = useRef<
    ((geometry: TaskGeometry) => void) | undefined
  >(undefined);
  const [drawingSource, setDrawingSource] = useState<
    "iconbar" | "layer" | null
  >(null);

  // Highlighted feature state
  const [highlightedFeature, setHighlightedFeature] =
    useState<HighlightedFeature | null>(null);

  const clearHighlight = useCallback(() => {
    setHighlightedFeature(null);
  }, []);

  const [dynamicArcgisLayers, setDynamicArcgisLayers] = useState<
    ArcgisLayerConfig[]
  >([]);
  const [arcgisLayersVisible, setArcgisLayersVisible] = useState<
    Record<string, boolean>
  >({});

  const ARCGIS_LAYERS_STORAGE_KEY = "taskr-arcgis-layers-visibility";

  const loadArcgisLayerVisibility = (): Record<string, boolean> => {
    try {
      const saved = localStorage.getItem(ARCGIS_LAYERS_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn(
        "Error loading ArcGIS layer visibility from localStorage:",
        error
      );
    }
    return {};
  };

  const saveArcgisLayerVisibility = (visibility: Record<string, boolean>) => {
    try {
      localStorage.setItem(
        ARCGIS_LAYERS_STORAGE_KEY,
        JSON.stringify(visibility)
      );
    } catch (error) {
      console.warn(
        "Error saving ArcGIS layer visibility to localStorage:",
        error
      );
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(BASEMAP_STORAGE_KEY, selectedBasemapKey);
    } catch (error) {
      console.warn("Error saving basemap to localStorage:", error);
    }
  }, [selectedBasemapKey]);

  useEffect(() => {
    fetchArcgisLayers()
      .then((layers) => {
        setDynamicArcgisLayers(layers);
        const savedVisibility = loadArcgisLayerVisibility();
        const visibilityState: Record<string, boolean> = {};
        layers.forEach((layer) => {
          if (layer.key in savedVisibility) {
            visibilityState[layer.key] = savedVisibility[layer.key];
          } else {
            visibilityState[layer.key] = layer.visible;
          }
        });
        setArcgisLayersVisible(visibilityState);
      })
      .catch((error) => {
        console.error("Failed to fetch ArcGIS layers:", error);
        setDynamicArcgisLayers([]);
        setArcgisLayersVisible({});
      });
  }, []);

  const toggleArcgisLayer = (layerKey: string) => {
    setArcgisLayersVisible((prev) => {
      const newVisibility = {
        ...prev,
        [layerKey]: !prev[layerKey],
      };
      saveArcgisLayerVisibility(newVisibility);
      return newVisibility;
    });
  };

  const refreshLayers = useCallback(async () => {
    try {
      const layers = await fetchArcgisLayers();
      setDynamicArcgisLayers(layers);

      const visibilityState: Record<string, boolean> = {
        ...arcgisLayersVisible,
      };
      layers.forEach((layer) => {
        if (!(layer.key in visibilityState)) {
          visibilityState[layer.key] = layer.visible;
        }
      });
      setArcgisLayersVisible(visibilityState);
    } catch (error) {
      console.error("Failed to refresh ArcGIS layers:", error);
    }
  }, [arcgisLayersVisible]);

  const getVisibleArcgisLayers = useMemo(() => {
    return () => {
      const layersWithVisibility = dynamicArcgisLayers
        .filter((layer) => layer.key !== TASK_LAYER_KEY)
        // Filter out layers where visible is false in the database configuration
        .filter((layer) => layer.visible !== false)
        .map((layer) => ({
          ...layer,
          visible: arcgisLayersVisible[layer.key] ?? layer.visible,
        }));

      const sortedLayers = layersWithVisibility.sort((a, b) => {
        const aOrder = a.display_order ?? 999;
        const bOrder = b.display_order ?? 999;
        return aOrder - bOrder;
      });

      return sortedLayers;
    };
  }, [arcgisLayersVisible, dynamicArcgisLayers]);

  const disableAllDrawModes = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    try {
      draw.changeMode("simple_select");
    } catch (error) {
      console.warn("[MapContext] Failed to disable draw modes:", error);
    }
  }, []);

  const startDrawing = (
    type: DrawMode,
    source: "iconbar" | "layer" = "iconbar"
  ) => {
    const draw = drawRef.current;

    if (!draw) {
      console.error("[MapContext] Mapbox Draw instance not available");
      return;
    }

    setIsDrawing(true);
    isDrawingRef.current = true; // Update ref synchronously
    setDrawingSource(source);

    disableAllDrawModes();

    const drawModeMap: Record<DrawMode, string> = {
      point: "draw_point",
      line: "draw_line_string",
      polygon: "draw_polygon",
    };

    const drawMode = drawModeMap[type];

    try {
      draw.changeMode(drawMode);
    } catch (error) {
      console.error("[MapContext] Failed to start drawing mode:", error);
      setIsDrawing(false);
      isDrawingRef.current = false; // Update ref synchronously
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    isDrawingRef.current = false; // Update ref synchronously
    setDrawingSource(null);
    disableAllDrawModes();
  };

  const removeDrawnLayer = useCallback(() => {
    const feature = drawnFeatureRef.current;
    const draw = drawRef.current;

    // Remove feature using Mapbox Draw API
    if (feature && draw) {
      try {
        removeDrawFeatureSafely(draw, feature.id, "removeDrawnLayer");
        drawnFeatureRef.current = null;
      } catch (error) {
        console.warn("Failed to remove drawn feature:", error);
      }
    }

    // Also clean up any orphaned draw layers
    const map = mapInstanceRef.current;
    if (map && draw) {
      try {
        // Delete all features from draw
        const allFeatures = draw.getAll();
        if (allFeatures.features.length > 0) {
          const featureIds = allFeatures.features.map((f) => f.id as string);
          draw.delete(featureIds);
        }
      } catch (error) {
        console.warn("Failed to clean up draw features:", error);
      }
    }

    setDrawnFeature(null);
  }, []);

  const clearDrawnGeometry = useCallback(() => {
    setDrawnGeometry(null);
    setDrawingSource(null);
    removeDrawnLayer();
  }, [removeDrawnLayer]);

  const registerMapInstance = useCallback(
    (map: MapLibreMap) => {
      if (mapInstanceRef.current === map) return;

      mapInstanceRef.current = map;

      drawCleanupRef.current?.();

      // Initialize Mapbox GL Draw with custom styles for branding colors
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {},
        // Allow polygons to complete on Enter key or double-click
        clickBuffer: 2,
        touchBuffer: 25,
        styles: [
          // Polygon fill - Marian Blue (#374795) - inactive (completed)
          {
            id: "gl-draw-polygon-fill-inactive",
            type: "fill",
            filter: [
              "all",
              ["==", "active", "false"],
              ["==", "$type", "Polygon"],
              ["!=", "mode", "static"],
            ],
            paint: {
              "fill-color": "#374795",
              "fill-opacity": 0.3,
            },
          },
          // Polygon fill - active (while drawing)
          {
            id: "gl-draw-polygon-fill-active",
            type: "fill",
            filter: [
              "all",
              ["==", "active", "true"],
              ["==", "$type", "Polygon"],
            ],
            paint: {
              "fill-color": "#374795",
              "fill-opacity": 0.3,
            },
          },
          // Polygon outline - Marian Blue (#374795)
          {
            id: "gl-draw-polygon-stroke-inactive",
            type: "line",
            filter: [
              "all",
              ["==", "active", "false"],
              ["==", "$type", "Polygon"],
              ["!=", "mode", "static"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#374795",
              "line-width": 3,
            },
          },
          {
            id: "gl-draw-polygon-stroke-active",
            type: "line",
            filter: [
              "all",
              ["==", "active", "true"],
              ["==", "$type", "Polygon"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#374795",
              "line-width": 3,
            },
          },
          // LineString - Marian Blue (#374795) - CRITICAL FOR POLYLINE DRAWING
          {
            id: "gl-draw-line-inactive",
            type: "line",
            filter: [
              "all",
              ["==", "active", "false"],
              ["==", "$type", "LineString"],
              ["!=", "mode", "static"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#374795",
              "line-width": 4,
            },
          },
          {
            id: "gl-draw-line-active",
            type: "line",
            filter: [
              "all",
              ["==", "$type", "LineString"],
              ["==", "active", "true"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#374795",
              "line-width": 4,
            },
          },
          // Point/Circle - Gray (#6C757D)
          {
            id: "gl-draw-point-point-stroke-inactive",
            type: "circle",
            filter: [
              "all",
              ["==", "active", "false"],
              ["==", "$type", "Point"],
              ["==", "meta", "feature"],
              ["!=", "mode", "static"],
            ],
            paint: {
              "circle-radius": 8,
              "circle-opacity": 1,
              "circle-color": "#374795",
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          },
          {
            id: "gl-draw-point-active",
            type: "circle",
            filter: [
              "all",
              ["==", "$type", "Point"],
              ["==", "active", "true"],
              ["!=", "meta", "midpoint"],
            ],
            paint: {
              "circle-radius": 8,
              "circle-color": "#374795",
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          },
          // Vertex halo layers to keep vertices visible over lines
          {
            id: "gl-draw-polygon-and-line-vertex-halo-inactive",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["==", "active", "false"],
            ],
            paint: {
              "circle-radius": 7,
              "circle-color": "#ffffff",
              "circle-opacity": 1,
            },
          },
          {
            id: "gl-draw-polygon-and-line-vertex-halo-active",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["==", "active", "true"],
            ],
            paint: {
              "circle-radius": 8.5,
              "circle-color": "#ffffff",
              "circle-opacity": 1,
            },
          },
          // Vertex points for lines and polygons - Marian Blue (#374795)
          // Use active flag so all prior vertices stay visible
          {
            id: "gl-draw-polygon-and-line-vertex-inactive",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["==", "active", "false"],
            ],
            paint: {
              "circle-radius": 5.5,
              "circle-color": "#374795",
              "circle-stroke-color": "#374795",
              "circle-stroke-width": 1.5,
              "circle-opacity": 1,
            },
          },
          {
            id: "gl-draw-polygon-and-line-vertex-active",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["==", "active", "true"],
            ],
            paint: {
              "circle-radius": 7,
              "circle-color": "#374795",
              "circle-stroke-color": "#374795",
              "circle-stroke-width": 2,
              "circle-opacity": 1,
            },
          },
          // Midpoint vertices (for adding points between existing ones)
          {
            id: "gl-draw-polygon-midpoint",
            type: "circle",
            filter: [
              "all",
              ["==", "$type", "Point"],
              ["==", "meta", "midpoint"],
            ],
            paint: {
              "circle-radius": 3,
              "circle-color": "#374795",
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 1.5,
            },
          },
        ],
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.addControl(draw as any);
      drawRef.current = draw;

      // Track the last processed feature ID to prevent duplicate processing
      let lastProcessedFeatureId: string | null = null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handleDrawCreate = (e: any) => {
        // draw.create event received

        const features = e.features;
        if (!features || features.length === 0) {
          console.warn("[Drawing] No features in draw.create event");
          return;
        }

        const feature = features[0];

        // Prevent processing the same feature multiple times
        if (feature.id === lastProcessedFeatureId) {
          return;
        }
        lastProcessedFeatureId = feature.id;

        const geometry = feature.geometry as TaskGeometry;

        if (!geometry) {
          console.warn("[Drawing] No geometry in drawn feature");
          return;
        }

        // Feature created

        setDrawnGeometry(geometry);
        setDrawnFeature(feature);

        // IMPORTANT: Delay setting isDrawing to false to prevent the click event
        // that created the geometry from triggering layer popups
        // The click event needs to see isDrawing=true to be blocked
        const callback = onGeometryDrawnRef.current;

        setTimeout(() => {
          setIsDrawing(false);
          isDrawingRef.current = false; // Update ref synchronously
          setDrawingSource(null);
          lastProcessedFeatureId = null; // Reset for next drawing

          // Call the callback after drawing state settles so consumers
          // (e.g. MapPage) can safely inspect isDrawingRef in the same tick
          if (callback) {
            callback(geometry);
          }
        }, 100);

        // Disable draw mode
        disableAllDrawModes();
      };

      // Also listen for other draw events
      const handleDrawModeChange = () => {
        // mode changed
      };

      map.on("draw.create", handleDrawCreate);
      map.on("draw.modechange", handleDrawModeChange);

      drawCleanupRef.current = () => {
        try {
          map.off("draw.create", handleDrawCreate);
          map.off("draw.modechange", handleDrawModeChange);
        } catch {
          /* ignore */
        }
        try {
          if (drawRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map.removeControl(drawRef.current as any);
          }
        } catch {
          /* ignore */
        }
        drawRef.current = null;
      };
    },
    [disableAllDrawModes]
  );

  const unregisterMapInstance = useCallback(() => {
    drawCleanupRef.current?.();
    drawCleanupRef.current = null;
    drawRef.current = null;
    mapInstanceRef.current = null;
    clearDrawnGeometry();
  }, [clearDrawnGeometry]);

  useEffect(() => {
    return () => {
      unregisterMapInstance();
    };
  }, [unregisterMapInstance]);

  const setOnGeometryDrawn = useCallback(
    (callback: ((geometry: TaskGeometry) => void) | undefined) => {
      onGeometryDrawnRef.current = callback;
    },
    []
  );

  return (
    <MapContext.Provider
      value={{
        mapRef,
        mapInstance: mapInstanceRef,
        registerMapInstance,
        unregisterMapInstance,
        showBasemapSwitcher,
        setShowBasemapSwitcher,
        tableVisible,
        setTableVisible,
        taskLayerVisible,
        setTaskLayerVisible,
        selectedBasemapKey,
        setSelectedBasemapKey,
        arcgisLayersVisible,
        toggleArcgisLayer,
        getVisibleArcgisLayers,
        refreshLayers,
        isDrawing,
        isDrawingRef, // Export ref for synchronous access in event handlers
        drawnGeometry,
        drawnFeature,
        startDrawing,
        stopDrawing,
        clearDrawnGeometry,
        removeDrawnLayer,
        onGeometryDrawn: onGeometryDrawnRef.current,
        setOnGeometryDrawn,
        drawingSource,
        highlightedFeature,
        setHighlightedFeature,
        clearHighlight,
      }}
    >
      {children}
    </MapContext.Provider>
  );
}
